<script setup>
import { ref, computed } from 'vue';
import { Card } from 'primevue';
import InputNumber from 'primevue/inputnumber';
import ANButton from '@/components/common/AnButton.vue';
import Chart from 'primevue/chart';
import { calculateStepUpSIP } from '@/utils/financeCalculators.js';

// Form data
const formData = ref({
  initialSIP: 5000,
  durationYears: 10,
  rate: 12,
  stepUpPercent: 10
});

const result = ref(null);
const chartData = ref({});
const chartOptions = ref({});

// Computed properties
const isValidInput = computed(() => {
  return formData.value.initialSIP > 0 && 
         formData.value.durationYears > 0 && 
         formData.value.rate > 0 &&
         formData.value.stepUpPercent >= 0;
});

// Calculate Step-up SIP
const calculate = () => {
  if (!isValidInput.value) return;

  result.value = calculateStepUpSIP(formData.value);
  if (!result.value.error) {
    generateChart();
    generateComparisonData();
    // Scroll to results section after calculation
    setTimeout(() => {
      const resultsElement = document.querySelector('.result-card');
      if (resultsElement) {
        resultsElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      }
    }, 100);
  }
};

// Generate chart data
const generateChart = () => {
  const years = [];
  const stepUpInvestment = [];
  const regularSIPInvestment = [];
  
  let stepUpTotal = 0;
  let regularTotal = 0;
  let currentSIP = formData.value.initialSIP;
  
  const r = formData.value.rate / 100 / 12;

  for (let year = 1; year <= formData.value.durationYears; year++) {
    // Step-up SIP calculation
    for (let month = 1; month <= 12; month++) {
      const monthsPassed = (year - 1) * 12 + month;
      const remainingMonths = formData.value.durationYears * 12 - monthsPassed + 1;
      stepUpTotal += currentSIP * Math.pow(1 + r, remainingMonths);
    }
    
    // Regular SIP calculation
    for (let month = 1; month <= 12; month++) {
      const monthsPassed = (year - 1) * 12 + month;
      const remainingMonths = formData.value.durationYears * 12 - monthsPassed + 1;
      regularTotal += formData.value.initialSIP * Math.pow(1 + r, remainingMonths);
    }
    
    years.push(`Year ${year}`);
    stepUpInvestment.push(Math.round(stepUpTotal));
    regularSIPInvestment.push(Math.round(regularTotal));
    
    // Increase SIP for next year
    currentSIP *= (1 + formData.value.stepUpPercent / 100);
  }

  chartData.value = {
    labels: years,
    datasets: [
      {
        label: 'Step-up SIP',
        data: stepUpInvestment,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        fill: false
      },
      {
        label: 'Regular SIP',
        data: regularSIPInvestment,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        fill: false
      }
    ]
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Step-up SIP vs Regular SIP Growth'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + (value / 100000).toFixed(1) + 'L';
          }
        }
      }
    }
  };
};

// Generate comparison data
const generateComparisonData = () => {
  // Calculate regular SIP for comparison
  const regularSIPResult = formData.value.initialSIP * 
    (((1 + formData.value.rate / 100 / 12) ** (formData.value.durationYears * 12) - 1) * 
     (1 + formData.value.rate / 100 / 12)) / (formData.value.rate / 100 / 12);

  result.value.regularSIPValue = Math.round(regularSIPResult);
  result.value.extraGain = result.value.futureValue - result.value.regularSIPValue;
  
  // Calculate total invested amounts
  let stepUpInvested = 0;
  let currentSIP = formData.value.initialSIP;
  
  for (let year = 0; year < formData.value.durationYears; year++) {
    stepUpInvested += currentSIP * 12;
    currentSIP *= (1 + formData.value.stepUpPercent / 100);
  }
  
  result.value.totalInvested = Math.round(stepUpInvested);
  result.value.regularInvested = formData.value.initialSIP * formData.value.durationYears * 12;
};

// Initialize
calculate();
</script>

<template>
  <div class="stepup-sip-calculator">
    <div class="calculator-grid">
      <!-- Input Form -->
      <Card class="input-card">
        <template #title>
          <h3>Step-up SIP Calculator Input</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- Initial SIP Amount -->
            <div class="field">
              <label for="initialSIP">Initial Monthly SIP (₹)</label>
              <InputNumber
                id="initialSIP"
                v-model="formData.initialSIP"
                :min="100"
                :max="1000000"
                :step="500"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Step-up Percentage -->
            <div class="field">
              <label for="stepUp">Annual Step-up (%)</label>
              <InputNumber
                id="stepUp"
                v-model="formData.stepUpPercent"
                :min="0"
                :max="50"
                :step="1"
                :minFractionDigits="0"
                :maxFractionDigits="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Recommended: 10-15% to beat inflation</small>
            </div>

            <!-- Duration -->
            <div class="field">
              <label for="duration">Investment Duration (Years)</label>
              <InputNumber
                id="duration"
                v-model="formData.durationYears"
                :min="1"
                :max="50"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Rate -->
            <div class="field">
              <label for="rate">Expected Annual Return (%)</label>
              <InputNumber
                id="rate"
                v-model="formData.rate"
                :min="1"
                :max="30"
                :step="0.5"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Calculate Button -->
            <ANButton
              label="Calculate"
              severity="primary"
              size="large"
              @click="calculate"
              :disabled="!isValidInput"
              class="calculate-btn"
            />
          </div>
        </template>
      </Card>

      <!-- Results -->
      <Card v-if="result && !result.error" class="result-card">
        <template #title>
          <h3>Step-up SIP Results</h3>
        </template>
        <template #content>
          <div class="results">
            <div class="result-item highlight">
              <div class="result-label">Future Value (Step-up SIP)</div>
              <div class="result-value">₹{{ result.futureValue.toLocaleString('en-IN') }}</div>
            </div>

            <div class="comparison-section">
              <h4>Comparison with Regular SIP</h4>
              <div class="comparison-grid">
                <div class="comparison-item">
                  <div class="comp-label">Regular SIP Value</div>
                  <div class="comp-value">₹{{ result.regularSIPValue?.toLocaleString('en-IN') }}</div>
                </div>
                <div class="comparison-item gain">
                  <div class="comp-label">Extra Gain from Step-up</div>
                  <div class="comp-value">₹{{ result.extraGain?.toLocaleString('en-IN') }}</div>
                </div>
              </div>
            </div>

            <div class="result-summary">
              <div class="summary-item">
                <span class="summary-label">Total Invested (Step-up):</span>
                <span class="summary-value">₹{{ result.totalInvested?.toLocaleString('en-IN') }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Total Invested (Regular):</span>
                <span class="summary-value">₹{{ result.regularInvested?.toLocaleString('en-IN') }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Wealth Gain:</span>
                <span class="summary-value gain">
                  ₹{{ (result.futureValue - result.totalInvested).toLocaleString('en-IN') }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- SIP Progression Table -->
    <Card v-if="result && !result.error" class="progression-card">
      <template #title>
        <h3>Annual SIP Progression</h3>
      </template>
      <template #content>
        <div class="progression-table">
          <div class="table-header">
            <span>Year</span>
            <span>Monthly SIP</span>
            <span>Annual Investment</span>
            <span>Cumulative Investment</span>
          </div>
          <div 
            v-for="year in formData.durationYears" 
            :key="year"
            class="table-row"
          >
            <span>{{ year }}</span>
            <span>₹{{ (formData.initialSIP * Math.pow(1 + formData.stepUpPercent / 100, year - 1)).toLocaleString('en-IN') }}</span>
            <span>₹{{ (formData.initialSIP * Math.pow(1 + formData.stepUpPercent / 100, year - 1) * 12).toLocaleString('en-IN') }}</span>
            <span>₹{{ (() => {
              let cumulative = 0;
              for (let y = 1; y <= year; y++) {
                cumulative += formData.initialSIP * Math.pow(1 + formData.stepUpPercent / 100, y - 1) * 12;
              }
              return cumulative.toLocaleString('en-IN');
            })() }}</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Chart -->
    <Card v-if="result && !result.error && chartData.labels" class="chart-card">
      <template #title>
        <h3>Step-up SIP vs Regular SIP Comparison</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="line" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Error Display -->
    <Card v-if="result && result.error" class="error-card">
      <template #content>
        <div class="error-message">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ result.error }}</span>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.stepup-sip-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.input-field {
  width: 100%;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
}

.result-item.highlight {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
}

.result-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  opacity: 0.9;
}

.result-value {
  font-size: 2rem;
  font-weight: 700;
}

.comparison-section h4 {
  margin: 0 0 16px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.comparison-item {
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  background: var(--an-background-light);
  border: 1px solid var(--an-gray3);
}

.comparison-item.gain {
  background: var(--an-success-light);
  border-color: var(--an-success);
}

.comp-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--an-text-secondary);
  margin-bottom: 4px;
}

.comp-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

.comparison-item.gain .comp-value {
  color: var(--an-success);
}

.result-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--an-gray3);
}

.summary-label {
  color: var(--an-text-secondary);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: var(--an-text-primary);
}

.summary-value.gain {
  color: var(--an-success);
}

.progression-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 60px 120px 140px 1fr;
  gap: 16px;
  padding: 12px;
  align-items: center;
}

.table-header {
  font-weight: 700;
  background: var(--an-background-light);
  border-radius: 6px;
  color: var(--an-text-primary);
  position: sticky;
  top: 0;
}

.table-row {
  border-bottom: 1px solid var(--an-gray3);
  font-size: 14px;
}

.table-row:hover {
  background: var(--an-background-light);
}

.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

.error-card {
  background: var(--an-error-light);
  border: 1px solid var(--an-error);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--an-error);
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calculator-grid {
    grid-template-columns: 1fr;
  }
  
  .comparison-grid {
    grid-template-columns: 1fr;
  }
  
  .result-value {
    font-size: 1.5rem;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 50px 100px 120px 1fr;
    gap: 8px;
    font-size: 12px;
  }
  
  .chart-container {
    height: 300px;
  }
}
</style>
